import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cx } from "@/lib/utils";

export interface DropdownOption<T extends string | number = string> {
  value: T;
  label: ReactNode;
  description?: ReactNode;
  swatch?: ReactNode;
  disabled?: boolean;
  trailing?: ReactNode;
}

interface DropdownProps<T extends string | number = string> {
  options: DropdownOption<T>[];
  value: T | null;
  onChange: (next: T) => void;
  placeholder?: ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  className?: string;
  testId?: string;
}

export function Dropdown<T extends string | number = string>({
  options,
  value,
  onChange,
  placeholder = "Vyberte…",
  ariaLabel,
  disabled = false,
  size = "md",
  className,
  testId,
}: DropdownProps<T>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const close = useCallback(() => {
    setOpen(false);
    setActiveIdx(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        close();
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  function pick(idx: number): void {
    const opt = options[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    close();
    buttonRef.current?.focus();
  }

  function handleButtonKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    // When closed: arrows / enter / space open the menu and seed the
    // active option to the current selection. When already open and
    // focus is still on the trigger (no listbox autofocus in jsdom),
    // arrows move the active option and enter picks it — so keyboard
    // users can drive the dropdown without ever leaving the button.
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
        const startIdx = options.findIndex((o) => o.value === value);
        setActiveIdx(startIdx >= 0 ? startIdx : 0);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, Math.max(0, i + 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (activeIdx >= 0) pick(activeIdx);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(options.length - 1);
    }
  }

  function handleListKeyDown(e: KeyboardEvent<HTMLUListElement>): void {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (activeIdx >= 0) pick(activeIdx);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveIdx(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setActiveIdx(options.length - 1);
    }
  }

  const sizeClass = size === "sm" ? "h-8 px-2.5 text-xs" : "h-10 px-3 text-sm";

  return (
    <div
      ref={containerRef}
      className={cx("relative inline-block", className)}
      data-testid={testId}
    >
      <button
        ref={buttonRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleButtonKeyDown}
        className={cx(
          "flex w-full items-center justify-between gap-2 rounded-md border border-border-strong bg-surface text-left text-ink transition-colors hover:border-ink-3 disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-4",
          sizeClass,
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {selected?.swatch}
          <span className="truncate">
            {selected ? selected.label : (
              <span className="text-ink-3">{placeholder}</span>
            )}
          </span>
        </span>
        <span
          aria-hidden="true"
          className={cx(
            "inline-block h-1.5 w-1.5 border-r border-b border-ink-3 transition-transform",
            open ? "rotate-[225deg]" : "rotate-45",
          )}
        />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={
            activeIdx >= 0 ? `${listboxId}-opt-${activeIdx}` : undefined
          }
          autoFocus
          onKeyDown={handleListKeyDown}
          // ``min-w-full`` matches the trigger as a floor, but the menu
          // grows to fit the widest option (no ``right-0`` clamp). Options
          // render whitespace-nowrap so their label drives the width.
          className="absolute left-0 top-full z-30 mt-1 max-h-72 min-w-full overflow-auto whitespace-nowrap rounded-md border border-border bg-surface py-1 shadow-lg focus:outline-none"
        >
          {options.map((opt, idx) => {
            const sel = opt.value === value;
            const active = idx === activeIdx;
            return (
              <li
                key={String(opt.value)}
                id={`${listboxId}-opt-${idx}`}
                role="option"
                aria-selected={sel}
                aria-disabled={opt.disabled || undefined}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => pick(idx)}
                className={cx(
                  "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm",
                  opt.disabled && "cursor-not-allowed text-ink-4",
                  !opt.disabled && active && "bg-surface-2",
                  sel && "font-medium text-ink",
                )}
              >
                {opt.swatch}
                <span className="flex-1">
                  <span className="block">{opt.label}</span>
                  {opt.description && (
                    <span className="block text-xs text-ink-3">
                      {opt.description}
                    </span>
                  )}
                </span>
                {opt.trailing}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
